(function () {
  'use strict';

  var MOBILE_MAX = 1024;
  var SCROLL_THRESHOLD = 8;
  var DIRECTION_DELTA = 5;
  var TOP_SHOW_THRESHOLD = 24;
  var SHOW_UP_THRESHOLD = 16;
  var SHOW_DELAY_MS = 500;
  var LAYOUT_LOCK_MS = 350;
  var SCROLL_END_MS = 120;

  var headerSection = null;
  var spacer = null;
  var topOffsetObserver = null;
  var lastScrollY = 0;
  var ticking = false;
  var isHidden = false;
  var isInitialized = false;
  var mediaQuery = null;
  var cachedHeight = 0;
  var cachedTopOffset = 0;
  var scrollAnchorY = 0;
  var showTimer = null;
  var scrollEndTimer = null;
  var layoutLockUntil = 0;
  var isUserScrolling = false;

  function isDesignMode() {
    return (
      (window.Shopify && window.Shopify.designMode) ||
      document.documentElement.classList.contains('shopify-design-mode')
    );
  }

  function isMobile() {
    return window.innerWidth <= MOBILE_MAX;
  }

  function getScrollY() {
    var y = window.scrollY || window.pageYOffset || 0;
    return y < 0 ? 0 : y;
  }

  function syncScrollPosition() {
    lastScrollY = getScrollY();
  }

  function getUpwardDistance(scrollY) {
    if (!isHidden || scrollAnchorY <= 0) return 0;
    return scrollAnchorY - scrollY;
  }

  function markScrollAnchor(scrollY) {
    scrollAnchorY = scrollY;
  }

  function isLayoutLocked() {
    return Date.now() < layoutLockUntil;
  }

  function lockLayoutAfterToggle() {
    layoutLockUntil = Date.now() + LAYOUT_LOCK_MS;
    syncScrollPosition();
  }

  function isSectionVisible(section) {
    if (!section) return false;
    if (section.getAttribute('aria-hidden') === 'true') return false;

    var style = window.getComputedStyle(section);
    if (style.display === 'none' || style.visibility === 'hidden') return false;

    return section.getBoundingClientRect().height > 0;
  }

  function getPromoSection() {
    var promoSection =
      document.getElementById('shopify-section-wls-promo-bar') ||
      document.querySelector('.shopify-section.wls-promo-bar');

    return promoSection && isSectionVisible(promoSection) ? promoSection : null;
  }

  function getTopOffset() {
    if (!headerSection) return 0;

    if (isInitialized) {
      var promoSection = getPromoSection();

      if (promoSection) {
        var promoBottom = promoSection.getBoundingClientRect().bottom;

        if (promoBottom > 0) {
          return Math.ceil(promoBottom);
        }
      }

      return 0;
    }

    var offset = 0;
    var node = headerSection.previousElementSibling;

    while (node) {
      if (node.classList && node.classList.contains('shopify-section') && isSectionVisible(node)) {
        var style = window.getComputedStyle(node);

        if (
          style.position === 'fixed' ||
          style.position === 'sticky' ||
          node.classList.contains('wls-promo-bar')
        ) {
          offset += Math.ceil(node.getBoundingClientRect().height);
        }
      }

      node = node.previousElementSibling;
    }

    return offset;
  }

  function getSafeAreaInsetTop() {
    if (getSafeAreaInsetTop.cached != null) return getSafeAreaInsetTop.cached;

    var probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;top:0;left:-9999px;visibility:hidden;padding-top:env(safe-area-inset-top, 0px);';
    document.body.appendChild(probe);
    var value = parseFloat(window.getComputedStyle(probe).paddingTop) || 0;
    document.body.removeChild(probe);

    getSafeAreaInsetTop.cached = value;
    return value;
  }

  function isSearchFocused() {
    if (!headerSection) return false;
    var active = document.activeElement;
    if (!active) return false;
    return headerSection.contains(active) && active.matches('[data-input-search], .wls-header__search-input');
  }

  function measureHeight(force) {
    if (!headerSection) return 0;
    if (!force && cachedHeight > 0) return cachedHeight;

    var wasHidden = headerSection.classList.contains('wls-mobile-header--hidden');
    if (wasHidden) {
      headerSection.classList.remove('wls-mobile-header--hidden');
    }

    cachedHeight = headerSection.offsetHeight;

    if (wasHidden) {
      headerSection.classList.add('wls-mobile-header--hidden');
    }

    return cachedHeight;
  }

  function updateLayoutMetrics() {
    var headerHeight = measureHeight(false);
    var topOffset = getTopOffset();
    var spacerHeight = isInitialized ? headerHeight : topOffset + headerHeight;

    cachedTopOffset = topOffset;

    document.documentElement.style.setProperty('--wls-mobile-header-top', topOffset + 'px');
    document.documentElement.style.setProperty('--wls-mobile-header-height', headerHeight + 'px');
    document.documentElement.style.setProperty('--wls-mobile-header-spacer-height', spacerHeight + 'px');
    document.documentElement.style.setProperty('--header-height', headerHeight + 'px');

    if (headerSection) {
      // Use the topOffset just computed above (plus the safe-area inset),
      // not a live getComputedStyle(...).top read — that CSS `top` has its
      // own transition (see .wls-mobile-header--initialized), so reading it
      // mid-animation (e.g. while a promo bar above is collapsing) bakes a
      // stale, in-between number into the hide transform. That fights with
      // `top` continuing to animate afterwards, producing a visible
      // stutter instead of a clean slide-off.
      document.documentElement.style.setProperty(
        '--wls-mobile-header-hide-y',
        (-(headerHeight + topOffset + getSafeAreaInsetTop())) + 'px'
      );
    }

    if (spacer) {
      spacer.style.height = spacerHeight + 'px';
    }
  }

  function cancelPendingShow() {
    if (showTimer) {
      window.clearTimeout(showTimer);
      showTimer = null;
    }
  }

  function scheduleShow() {
    if (showTimer || !isHidden) return;

    showTimer = window.setTimeout(function () {
      showTimer = null;

      if (!isHidden || isLayoutLocked()) return;

      if (getUpwardDistance(getScrollY()) >= SHOW_UP_THRESHOLD) {
        setHeaderVisible(true);
      }
    }, SHOW_DELAY_MS);
  }

  function setHeaderVisible(visible) {
    if (!headerSection || isHidden === !visible) return;

    isHidden = !visible;
    headerSection.classList.toggle('wls-mobile-header--hidden', isHidden);
    headerSection.classList.toggle('wls-mobile-header--visible', visible);

    if (isHidden) {
      markScrollAnchor(getScrollY());
    } else {
      scrollAnchorY = 0;
    }

    updateLayoutMetrics();
    lockLayoutAfterToggle();
  }

  function onScrollEnd() {
    isUserScrolling = false;
    scrollEndTimer = null;
    lastScrollY = getScrollY();
  }

  function onScroll() {
    if (!isInitialized || !isMobile()) {
      ticking = false;
      return;
    }

    var scrollY = getScrollY();
    var topOffset = getTopOffset();

    isUserScrolling = true;

    if (scrollEndTimer) {
      window.clearTimeout(scrollEndTimer);
    }

    scrollEndTimer = window.setTimeout(onScrollEnd, SCROLL_END_MS);

    if (topOffset !== cachedTopOffset) {
      updateLayoutMetrics();
    }

    headerSection.classList.toggle('wls-mobile-header--scrolled', scrollY > SCROLL_THRESHOLD);

    if (isLayoutLocked()) {
      lastScrollY = scrollY;
      ticking = false;
      return;
    }

    var delta = scrollY - lastScrollY;

    if (isSearchFocused()) {
      cancelPendingShow();
      setHeaderVisible(true);
    } else if (scrollY <= TOP_SHOW_THRESHOLD) {
      cancelPendingShow();
      setHeaderVisible(true);
    } else if (delta > DIRECTION_DELTA) {
      cancelPendingShow();
      setHeaderVisible(false);
    } else if (isHidden) {
      if (scrollY > scrollAnchorY) {
        markScrollAnchor(scrollY);
        cancelPendingShow();
      } else if (getUpwardDistance(scrollY) >= SHOW_UP_THRESHOLD) {
        scheduleShow();
      }
    }

    lastScrollY = scrollY;
    ticking = false;
  }

  function requestScrollTick() {
    if (!ticking) {
      ticking = true;
      window.requestAnimationFrame(onScroll);
    }
  }

  function observeTopBars() {
    if (!headerSection || topOffsetObserver) return;

    topOffsetObserver = new ResizeObserver(function () {
      cachedHeight = 0;
      updateLayoutMetrics();
      syncScrollPosition();
    });

    var node = headerSection.previousElementSibling;
    while (node) {
      if (node.classList && node.classList.contains('shopify-section')) {
        topOffsetObserver.observe(node);
      }
      node = node.previousElementSibling;
    }

    var promoSection = getPromoSection();

    if (promoSection) {
      topOffsetObserver.observe(promoSection);
    }
  }

  function disconnectTopBars() {
    if (topOffsetObserver) {
      topOffsetObserver.disconnect();
      topOffsetObserver = null;
    }
  }

  function init() {
    if (!isMobile() || isInitialized || isDesignMode()) return;

    headerSection =
      document.querySelector('.wls-section-header') ||
      document.querySelector('.t4s-section-header');
    if (!headerSection) return;

    spacer = document.createElement('div');
    spacer.className = 'wls-mobile-header__spacer';
    spacer.setAttribute('aria-hidden', 'true');
    headerSection.parentNode.insertBefore(spacer, headerSection);

    headerSection.classList.add('wls-mobile-header--initialized', 'wls-mobile-header--visible');
    document.documentElement.classList.add('wls-mobile-header-sticky-active');
    isInitialized = true;
    cachedHeight = 0;
    updateLayoutMetrics();
    syncScrollPosition();
    observeTopBars();

    window.addEventListener('scroll', requestScrollTick, { passive: true });

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', function () {
        cachedHeight = 0;
        updateLayoutMetrics();
        syncScrollPosition();
      });
    }
  }

  function teardown() {
    if (!isInitialized) return;

    cancelPendingShow();

    if (scrollEndTimer) {
      window.clearTimeout(scrollEndTimer);
      scrollEndTimer = null;
    }

    window.removeEventListener('scroll', requestScrollTick);
    disconnectTopBars();

    if (spacer && spacer.parentNode) {
      spacer.parentNode.removeChild(spacer);
    }

    if (headerSection) {
      headerSection.classList.remove(
        'wls-mobile-header--initialized',
        'wls-mobile-header--visible',
        'wls-mobile-header--hidden',
        'wls-mobile-header--scrolled'
      );
    }

    document.documentElement.classList.remove('wls-mobile-header-sticky-active');
    document.documentElement.style.removeProperty('--wls-mobile-header-top');
    document.documentElement.style.removeProperty('--wls-mobile-header-height');
    document.documentElement.style.removeProperty('--wls-mobile-header-spacer-height');
    document.documentElement.style.removeProperty('--wls-mobile-header-hide-y');
    document.documentElement.style.removeProperty('--header-height');
    headerSection = null;
    spacer = null;
    isInitialized = false;
    isHidden = false;
    isUserScrolling = false;
    ticking = false;
    cachedHeight = 0;
    cachedTopOffset = 0;
    layoutLockUntil = 0;
    scrollAnchorY = 0;
  }

  function handleResize() {
    if (isMobile() && !isDesignMode()) {
      if (!isInitialized) {
        init();
      } else {
        cachedHeight = 0;
        updateLayoutMetrics();
        syncScrollPosition();
      }
    } else {
      teardown();
    }
  }

  function bootstrap() {
    if (isDesignMode()) return;

    mediaQuery = window.matchMedia('(max-width: ' + MOBILE_MAX + 'px)');
    handleResize();
    mediaQuery.addEventListener('change', handleResize);
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', function () {
      window.setTimeout(function () {
        getSafeAreaInsetTop.cached = null;
        handleResize();
      }, 150);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
